// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder.
//
// How a piece of information is held is said by its type: in a system (a database, an application or a service), as a
// file, on paper or by word of mouth. Typed as plain information, it says nothing, and the friction and risk read from
// how information moves (re-keying, manual bridges, unofficial copies) can't see it. Until the user says, LINQ reads a
// type, as it reads a system's kind from its name:
// - from a channel attached to it that carries information one way: a phone, word of mouth; a printer, the post or a
//   fax, paper; a scanner, a file
// - else from its name: a call or a conversation is spoken; a form, a letter or a printout is paper; a spreadsheet, an
//   export or a PDF is a file
// A system of record or business system attached to it says it's held in that system, so nothing is read from its name
import { LinqsetNode, NODE_TYPE, SYSTEM_KIND } from "./linqset-base"
import { systemKindOf } from "./system-kind"

type SystemLike = Pick<LinqsetNode, 'name' | 'type'> & { properties?: { systemKind?: SYSTEM_KIND | null } }

const VERBAL_CHANNEL = /\b(phones?|telephones?|mobiles?|cell ?phones?|voicemail|call cent(re|er) phones?)\b/i
const PAPER_CHANNEL = /\b(printers?|post|postal|mail ?room|fax)\b/i
const FILE_CHANNEL = /\b(scanners?)\b/i

const VERBAL_NAME = /\b(calls?|phone calls?|conversations?|verbal|spoken|word of mouth|voicemails?|in person|face to face|discussions?|verbally)\b/i
// a record of what was said is written down, not spoken
const WRITTEN = /\b(notes?|logs?|records?|minutes|summary|summaries|transcripts?)\b/i
// a form or a letter online is digital, not paper
const DIGITAL = /\b(web|online|e-?forms?|e-?mails?|digital|electronic|portal|app)\b/i
// a file's own words first, so a PDF form is a file
const FILE_NAME = /\b(spreadsheets?|excel|xlsx?|csv|exports?|extracts?|pdfs?|attachments?|scans?|scanned|files?)\b/i
const PAPER_NAME = /\b(paper|forms?|letters?|printouts?|printed|slips?|dockets?|cheques?|hard ?copy|handwritten|signed|posted|faxed)\b/i

// LINQ's read of how information is held, from the systems attached to it and its name; nothing where neither says
export const suggestInformationType = (name: string | undefined, systems: SystemLike[] = []): NODE_TYPE | undefined => {
    const names = systems.map(s => s.name || '')
    if (names.some(n => VERBAL_CHANNEL.test(n))) return NODE_TYPE.VERBAL
    if (names.some(n => PAPER_CHANNEL.test(n))) return NODE_TYPE.PAPER
    if (names.some(n => FILE_CHANNEL.test(n))) return NODE_TYPE.FILE
    // held in a system of record or a business system: its kind says how, not the name
    if (systems.some(s => systemKindOf(s).kind !== SYSTEM_KIND.LOCAL)) return undefined
    const n = name || ''
    if (VERBAL_NAME.test(n) && !WRITTEN.test(n)) return NODE_TYPE.VERBAL
    if (FILE_NAME.test(n)) return NODE_TYPE.FILE
    if (PAPER_NAME.test(n) && !DIGITAL.test(n)) return NODE_TYPE.PAPER
    return undefined
}

// a piece of information's type: as the user set it, or, typed as plain information, LINQ's read where it has one
export const informationTypeOf = (node: Pick<LinqsetNode, 'name' | 'type'>, systems: SystemLike[] = []): { type: NODE_TYPE; read: boolean } => {
    if (node.type !== undefined && node.type !== NODE_TYPE.INFORMATION) return { type: node.type, read: false }
    const read = suggestInformationType(node.name, systems)
    return read !== undefined ? { type: read, read: true } : { type: NODE_TYPE.INFORMATION, read: false }
}
